import React from 'react';
import PropTypes from 'prop-types';
import getStyles from '@front10/helpers/dist/UseStyles';

//-------------------------------------
// Basic components here
//-------------------------------------
import Header from '../../elements/Header';
import DatesRangeSelector from '../../controls/DatesRangeSelectorMobile';
import Row from '../../elements/Row';
import Col from '../../elements/Col';
import Box from '../../elements/Box';
import Button from '../../elements/Button';
import Icon from '../../elements/Icon';
import Text from '../../elements/Text';
import FareListTwoColumns from '../../lists/FareListTwoColumns';
import Budget from '../../controls/Budget';
import CheckList from '../../controls/CheckList';
import LocationSelector from '../../controls/LocationSelector';
import OfferHorizontalCard from '../../items/OfferHorizontalCard';
import Disclaimer from '../../others/Disclaimer';
import Skeleton from '../../others/Skeleton/SkeletonFeaturedDestinationsMobile';
import Loading from '../../others/Loading';
import NoFeaturedPopularFaresAlert from '../../alerts/NoFeaturedPopularFaresAlert';
import { isEmpty } from '../../../helpers/utils';
import withDeepMergeDefaultProps from '../../utils/WithDeepMergeDefaultProps';
import NavigationHeader from '../../elements/NavigationHeader';
import ContainerErrorAlert from '../../alerts/ContainerErrorAlert';
import AirBnBIcon from '../../elements/AirBnbIcon';
import GuestsSelectorMobile from '../../controls/GuestsSelectorMobile';

// airbnb actions
import detailHouse from '../../../_integrations/airbnb/actions';

//-------------------------------------
// Every mundo integration
//-------------------------------------
import { getOffers } from '../../../_integrations/airbnb/listings';
import { getAirports as getCities } from '../../../_integrations/everymundo/airports';
import {
  hookFaresOnFilterChange,
  hookComponentDidMount,
  hookOnErrorAlert
} from '../_commons/featuredOffersHook';

//-------------------------------------
// Integration Layer Default Actions
//-------------------------------------
import useChangeOrigin from '../../../_integrations/everymundo/tracking/_experimental/events/useChangeOrigin';
import useChangeDateRange from '../../../_integrations/everymundo/tracking/_experimental/events/useChangeDateRange';
import useChangeBudget from '../../../_integrations/everymundo/tracking/_experimental/events/useChangeBudget';
import useChangeHomeType from '../../../_integrations/everymundo/tracking/_experimental/events/useChangeHomeType';
import useChangeGuests from '../../../_integrations/everymundo/tracking/_experimental/events/useChangeGuests';
import useClickListing from '../../../_integrations/everymundo/tracking/_experimental/events/useClickListing';

//-------------------------------------
// Commons functions
//-------------------------------------
import { computeDateRange } from '../../../helpers/date';
import { enterFullMode, exitFullMode } from '../_commons';
import { GuestsSelectorContainer } from '../../controls/GuestsSelector/commons';

const cardType = 'listing';

class AirBnBFeaturedDestinationsContainerMobile extends React.Component {
  state = {
    errorLoading: false,
    isLoadingOffers: false,
    departureAirports: this.props.data.initialDepartureAirports || [],
    offers: this.props.data.initialOffers || [],
    isSkeletonShown: this.props.showSkeletonScreen,
    origin: this.props.prePopulation.origin || '',
    dateRange: computeDateRange(this.props.prePopulation.dateRange),
    budget:
      this.props.prePopulation &&
      this.props.prePopulation.budget &&
      this.props.prePopulation.budget.maximum
        ? this.props.prePopulation.budget.maximum
        : null,
    homeType:
      this.props.prePopulation && this.props.prePopulation.homeType
        ? this.props.prePopulation.homeType
        : [],
    guests:
      this.props.prePopulation.guests && !isEmpty(this.props.prePopulation.guests)
        ? this.props.prePopulation.guests.adults + this.props.prePopulation.guests.children
        : 0,
    guestsDetails:
      this.props.prePopulation &&
      this.props.prePopulation.guests &&
      typeof this.props.prePopulation.guests === 'object' &&
      Object.keys(this.props.prePopulation.guests).length > 0
        ? this.props.prePopulation.guests
        : { adults: 0, children: 0, infants: 0 },
    currentComponent: 'all',
    previousComponent: 'all'
  };

  header = (
    <Header
      tag="h2"
      fontSize="6"
      content={
        this.props.components.title.content ||
        this.props.translate('cmp-featured-airbnb-container-title', 'TOP PLACES')
      }
      margin="0"
      {...this.props.components.title}
    />
  );

  skeleton = (<Skeleton {...this.props.skeletonSettings} position="absolute" width="100" />);

  disclaimer = (
    <Disclaimer
      labelKey="cmp-featured-listings-container-disclaimer"
      {...this.props.components.disclaimer}
    />
  );

  constructor(props) {
    super(props);
    this.dispatchGuests = React.createRef();
  }

  componentDidMount = () => {
    const { data, containerType } = this.props;
    if (containerType !== 'dumb') {
      // Preparing for the real hooks :)
      // we are using the "hook" concept here to reuse logic
      // between similar containers in case we need to
      if (data.initialOffers) {
        this.setState({
          isSkeletonShown: false
        });
      } else {
        hookComponentDidMount(this);
      }
    } else {
      hookComponentDidMount(this);
    }
  };

  navigationHeader = () => {
    const { translate, components } = this.props;
    const { currentComponent } = this.state;
    let titleHeaderNav = '';
    switch (currentComponent) {
      case 'origin':
        titleHeaderNav = translate(
          'cmp-location-selector-origin-header-mobile',
          'FROM City or Airport'
        );
        break;
      case 'rangeSelector':
        titleHeaderNav = translate('cmp-date-range-selector-placeholder', 'Any time');
        break;
      case 'guest':
        titleHeaderNav = translate('cmp-listings-carousel-container-mobile-guest', 'Guest');
        break;
      default:
        titleHeaderNav = translate('cmp-listings-carousel-container-mobile-filters', 'Filters');
    }
    let settings = { ...components.headerLocation };
    if (currentComponent === 'filters') {
      settings = { textAlign: 'left', fontWeight: 'bold', fontSize: '5', paddingLeft: '2' };
    }
    if (currentComponent === 'guest') {
      settings = {
        paddingRight: '0',
        fontWeight: 'bold',
        textAlign: 'center',
        fontSize: '5',
        paddingLeft: '5'
      };
    }
    return (
      <NavigationHeader
        headerLocation={settings}
        title={titleHeaderNav}
        onClickIcon={() => {
          this.goBack();
        }}
        styleIcon={{
          transform: 'rotate(90deg)',
          display: 'inline-block'
        }}
        {...components.headerSettings}
        showFilterIcon={currentComponent === 'filters'}
        showClearAll={currentComponent === 'filters' || currentComponent === 'guest'}
        onClickIconClearAll={this.handleClearAll}
      />
    );
  };

  handleClearAll = () => {
    const { currentComponent } = this.state;
    this.dispatchGuests.current({ type: 'CLEAR' });
    if (currentComponent === 'filters') {
      this.budget.reset();
      this.datesRangeSelector.reset();
      this.setState({
        origin: undefined,
        dateRange: {},
        budget: undefined,
        homeType: [],
        guests: 0,
        guestsDetails: { adults: 0, children: 0, infants: 0 }
      });
    }
  };

  renderAirport = (item) => {
    const { showCityCode } = this.props;
    const code = item.label ? item.label.substring(0, 3).toUpperCase() : '';
    return (
      <>
        {showCityCode && (
          <Box
            border
            paddingWidth="1"
            marginRight="2"
            floatLeft
            textAlign="center"
            fontWeight="bold"
            style={{ minWidth: '45px' }}
          >
            <span style={{ paddingTop: '2px' }}>{code}</span>
          </Box>
        )}
        <Box>
          <span>{item.label}</span>
        </Box>
      </>
    );
  };

  handleAirport = (origin) => {
    const { previousComponent } = this.state;
    this.props.onChangeDepartureCity(this.props, origin.value, cardType);
    this.setState({ origin: origin.value, currentComponent: previousComponent }, () => {
      if (previousComponent === 'all') {
        hookFaresOnFilterChange(this);
      }
    });
  };

  tryAgain = () => hookOnErrorAlert(this);

  clearAirport = () => {
    const { previousComponent, currentComponent } = this.state;
    this.setState({ origin: '' }, () => {
      if (previousComponent === 'all' && currentComponent !== 'filters') {
        hookFaresOnFilterChange(this);
      }
    });
  };

  onChangeDate = ({ start, end }) => {
    const { previousComponent } = this.state;
    this.props.onChangeDate(this.props, start, end, cardType);
    this.setState({ dateRange: { start, end }, currentComponent: previousComponent }, () => {
      if (previousComponent === 'all') {
        hookFaresOnFilterChange(this);
      }
    });
  };

  onClearDate = () => {
    const { previousComponent, currentComponent } = this.state;
    this.setState(
      ({ filtersBackup }) => ({
        dateRange: {},
        currentComponent: 'rangeSelector',
        filtersBackup: { ...filtersBackup, dateRange: {} }
      }),
      () => {
        if (previousComponent === 'all' && currentComponent !== 'filters') {
          hookFaresOnFilterChange(this);
        } else {
          enterFullMode(this);
        }
      }
    );
  };

  onChangeBudget = (value) => {
    const { currentComponent } = this.state;
    this.props.onChangeBudget(this.props, value, cardType);
    this.setState({ budget: value }, () => {
      if (currentComponent !== 'filters') {
        hookFaresOnFilterChange(this);
      }
    });
  };

  onClearBudget = () => {
    this.setState({ budget: null });
  };

  onChangeGuests = ({ adults, children, infants }) => {
    const { previousComponent } = this.state;
    const { onChangeGuests } = this.props;
    onChangeGuests(this.props, adults + children);
    this.setState(
      {
        guests: adults + children,
        guestsDetails: { adults, children, infants },
        currentComponent: previousComponent
      },
      () => {
        if (previousComponent === 'all') {
          hookFaresOnFilterChange(this);
        }
      }
    );
  };

  onClearGuests = () => {
    const { currentComponent } = this.state;
    this.setState({ guests: 0, guestsDetails: { adults: 0, children: 0, infants: 0 } }, () => {
      if (currentComponent === 'all') {
        hookFaresOnFilterChange(this);
      }
    });
  };

  onChangeHomeType = (homeType) => {
    this.props.onChangeHomeType(this.props, homeType);
    this.setState({ homeType });
  };

  goBack = () => {
    const { currentComponent, previousComponent } = this.state;
    const newState = {};
    if (currentComponent === 'filters') {
      newState.currentComponent = 'all';
      newState.previousComponent = 'all';
    } else if (
      currentComponent === 'origin' ||
      currentComponent === 'rangeSelector' ||
      currentComponent === 'guest'
    ) {
      newState.currentComponent = previousComponent;
      newState.previousComponent = 'all';
    }

    this.dismissFilters(newState, () => {
      this.dispatchGuests.current({ type: 'SET_VALUE', value: this.state.guestsDetails });
      this.datesRangeSelector.setValue(this.state.dateRange);
      if (this.state.currentComponent === 'all') {
        exitFullMode(this);
      }
    });
  };

  backupFilters = (newState = {}, callback = () => {}) => {
    this.setState(
      ({ origin, dateRange, budget, homeType, guests, guestsDetails }) => ({
        ...newState,
        filtersBackup: {
          origin,
          dateRange,
          budget,
          homeType,
          guests,
          guestsDetails
        }
      }),
      callback
    );
  };

  dismissFilters = (newState = {}, callback = () => {}) => {
    this.setState(
      (state) => ({
        ...newState,
        ...state.filtersBackup
      }),
      callback
    );
  };

  activeOrigin = () => {
    const { currentComponent } = this.state;
    if (currentComponent !== 'origin') {
      this.backupFilters(
        { currentComponent: 'origin', previousComponent: currentComponent },
        () => {
          enterFullMode(this);
        }
      );
    }
  };

  activeRangeSelector = () => {
    const { currentComponent } = this.state;
    if (currentComponent !== 'rangeSelector') {
      this.backupFilters(
        { currentComponent: 'rangeSelector', previousComponent: currentComponent },
        () => {
          enterFullMode(this);
        }
      );
    }
  };

  activeGuest = () => {
    const { currentComponent } = this.state;
    this.backupFilters({ currentComponent: 'guest', previousComponent: currentComponent }, () => {
      enterFullMode(this);
    });
  };

  activeMoreFilters = () => {
    const { currentComponent } = this.state;
    this.backupFilters({ currentComponent: 'filters', previousComponent: currentComponent }, () => {
      enterFullMode(this);
    });
  };

  apply = () => {
    this.setState({ currentComponent: 'all' }, () => {
      hookFaresOnFilterChange(this);
    });
  };

  /**
   *
   * @param {*} props
   * @returns The settings destinationItem more the settings needed to
   * executed tracking and modal opened
   * @memberof FeaturedDestinationContainer
   */
  getDestinationItemTracking = () => {
    return { onClick: this.handleFareItemClick, callToActionOnClick: this.handleFareItemClick };
  };

  /**
   *
   * @param {*} props
   * @returns The settings fareTwoList more the settings needed to executed tracking
   * @memberof FeaturedDestinationContainer
   */
  getFareTwoListTracking = () => {
    const {
      components: { featuredOfferItem }
    } = this.props;
    return {
      itemSettings: {
        ...featuredOfferItem,
        onClick: this.handleFareItemClick,
        callToActionOnClick: this.handleFareItemClick
      }
    };
  };

  handleFareItemClick = ({ data }) => {
    // Notifications via props (if Everymundo wants
    // to pass notifications they can do it via props)*/
    this.props.fareItemOnClickAction(this.props, data, this.state.dateRange);
  };

  render() {
    const {
      departureAirports,
      offers,
      isSkeletonShown,
      origin,
      isLoadingOffers,
      currentComponent,
      errorLoading,
      budget,
      dateRange,
      homeType,
      guestsDetails
    } = this.state;

    const {
      translate,
      direction,
      showSkeletonScreen,
      showLoading,
      showTitle,
      showFilters,
      showDisclaimer,
      components,
      theme
    } = this.props;
    const [className, style] = getStyles(this.props, { direction, theme });
    const styles = Object.assign({ minHeight: '600px' }, style);

    const featuredOffers = offers.slice(1);
    const mainFeaturedOffer = offers[0];

    const trackingDestinationItem = this.getDestinationItemTracking();
    const listItemsSettings = this.getFareTwoListTracking();

    const renderContent = (
      <Box
        position={currentComponent === 'all' && 'relative'}
        className={`${className} ${AirBnBFeaturedDestinationsContainerMobile.componentName}`}
        style={styles}
        display={currentComponent === 'rangeSelector' ? 'flex' : null}
        flexDirection={currentComponent === 'rangeSelector' ? 'column' : null}
        focusReferences={(ref) => {
          this.containerReference = ref;
        }}
        dataTestId="AirBnBFeaturedDestinationsContainer"
      >
        {isLoadingOffers && showLoading && <Loading {...components.loading} />}
        {showSkeletonScreen && isSkeletonShown && this.skeleton}
        {currentComponent === 'all' && showTitle && (
          <Row textAlign="center">
            <AirBnBIcon {...components.airBnBIconStyles} />
            <Col>{this.header}</Col>
          </Row>
        )}
        {showFilters && (
          <>
            {currentComponent !== 'all' && this.navigationHeader()}
            <Row
              {...components.filterContent}
              gutters
              flex={currentComponent === 'rangeSelector' ? '1' : null}
            >
              {(currentComponent === 'all' ||
                currentComponent === 'origin' ||
                currentComponent === 'filters') && (
                <Col xs="12">
                  {components.showLabelFilters && components.showLabelFilters && (
                    <Text {...components.labelFilters}>
                      {translate(
                        'cmp-listings-carousel-container-mobile-filters-destination',
                        'Destination'
                      )}
                    </Text>
                  )}
                  <LocationSelector
                    {...components.locationSelector}
                    onChange={this.handleAirport}
                    options={departureAirports}
                    placeholder={translate(
                      'cmp-location-destination-placeholder',
                      'Destination City'
                    )}
                    showCode={false}
                    itemRenderer={this.renderAirport}
                    defaultValue={origin}
                    onClear={this.clearAirport}
                    onFocus={this.activeOrigin}
                    onOpen={this.activeOrigin}
                    isMobile
                  />
                </Col>
              )}
              {(currentComponent === 'all' ||
                currentComponent === 'rangeSelector' ||
                currentComponent === 'filters') && (
                <Col
                  xs="12"
                  display={currentComponent === 'rangeSelector' ? 'flex' : null}
                  flexDirection={currentComponent === 'rangeSelector' ? 'column' : null}
                >
                  {components.showLabelFilters && components.showLabelFilters && (
                    <Text {...components.labelFilters}>
                      {translate('cmp-listings-carousel-container-mobile-filters-dates', 'Dates')}
                    </Text>
                  )}
                  <DatesRangeSelector
                    shadow="none"
                    {...components.datesRangeSelector}
                    onFocus={this.activeRangeSelector}
                    defaultValue={dateRange}
                    onChange={this.onChangeDate}
                    onClear={this.onClearDate}
                    departInputPlaceholderLabelKey="cmp-airbnb-date-date-range-from-placeholder"
                    returnInputPlaceholderLabelKey="cmp-airbnb-date-date-range-to-placeholder"
                    showPicker={currentComponent === 'rangeSelector'}
                    focusReferences={(ref) => {
                      this.datesRangeSelector = ref;
                    }}
                  />
                </Col>
              )}
              {(currentComponent === 'all' ||
                currentComponent === 'guest' ||
                currentComponent === 'filters') && (
                <Col xs="12" display="flex" flexDirection="column">
                  {components.showLabelFilters && components.showLabelFilters && (
                    <Text {...components.labelFilters}>
                      {translate('cmp-listings-carousel-container-mobile-guest', 'Guest')}
                    </Text>
                  )}
                  <GuestsSelectorContainer initialValue={guestsDetails}>
                    {([guests, dispatch]) => {
                      this.dispatchGuests.current = dispatch; // Escape hatch to sort the scope limitation of the render prop
                      return (
                        <GuestsSelectorMobile
                          {...components.guestsSelector}
                          onFocus={this.activeGuest}
                          showDetails={currentComponent === 'guest'}
                          dispatch={dispatch}
                          onApply={this.onChangeGuests}
                          onClear={this.onClearGuests}
                          value={guests}
                        />
                      );
                    }}
                  </GuestsSelectorContainer>
                </Col>
              )}
              {currentComponent === 'filters' && (
                <Col xs="12">
                  {components.showLabelFilters && components.showLabelFilters && (
                    <Text {...components.labelFilters}>
                      {translate('cmp-listings-carousel-container-mobile-filters-budget', 'Budget')}
                    </Text>
                  )}
                  <Budget
                    {...components.budgetSetting}
                    defaultValue={budget}
                    onChange={this.onChangeBudget}
                    onClear={this.onClearBudget}
                    focusReferences={(ref) => {
                      this.budget = ref;
                    }}
                  />
                </Col>
              )}
              {(currentComponent === 'hometype' || currentComponent === 'filters') && (
                <Col xs="12">
                  {components.showLabelFilters && components.showLabelFilters && (
                    <Text {...components.labelFilters}>
                      {translate('cmp-airbnb-home-type-label', 'Home Type')}
                    </Text>
                  )}
                  <CheckList
                    {...components.homeType}
                    showHeader={false}
                    onChange={this.onChangeHomeType}
                    value={homeType}
                  />
                </Col>
              )}
              {currentComponent === 'filters' && (
                <Col xs="12">
                  <Button {...components.applyFilters} onClick={this.apply}>
                    <Text {...components.applyFilters.label}>
                      {translate('cmp-listings-carousel-container-mobile-apply-filters', 'Apply')}
                    </Text>
                  </Button>
                </Col>
              )}
              {currentComponent === 'all' && (
                <Col xs="12">
                  <Button {...components.moreFilter} onClick={this.activeMoreFilters}>
                    <Icon {...components.moreFilter.icon} icon="settings" />
                    <Text {...components.moreFilter.label}>
                      {translate(
                        'cmp-listings-carousel-container-mobile-more-filters',
                        'More Filters'
                      )}
                    </Text>
                  </Button>
                </Col>
              )}
            </Row>
          </>
        )}
        <Row {...components.mainContent} gutters>
          {errorLoading && currentComponent === 'all' && (
            <Col>
              <ContainerErrorAlert
                tryAgain={this.tryAgain}
                labelKeySubTitleIntro="cmp-airbnb-error-reload-intro"
                labelKeySubTitle="cmp-text-tryagain"
                {...components.alert}
              />
            </Col>
          )}
          {!mainFeaturedOffer &&
            featuredOffers.length === 0 &&
            !errorLoading &&
            currentComponent === 'all' && (
              <Col>
                <NoFeaturedPopularFaresAlert
                  labelKeyTitle="cmp-alert-no-fares-available"
                  labelKeyLink="cmp-airbnb-alert-link-more-homes"
                  showLink
                  onLinkClick={detailHouse}
                  {...components.noResultsPlaceHolder}
                />
              </Col>
            )}
          {currentComponent === 'all' &&
            (featuredOffers.length !== 0 || mainFeaturedOffer) &&
            !errorLoading && (
              <Col>
                <Box {...components.mainFeaturedOfferLabel}>
                  <Text type="span">
                    {translate('cmp-featured-destination-container-most-popular', 'More Popular')}
                  </Text>
                </Box>
                {mainFeaturedOffer && (
                  <OfferHorizontalCard
                    marginBottom="2"
                    data={mainFeaturedOffer}
                    image={mainFeaturedOffer.destinationImage}
                    {...components.mainFeaturedOffer}
                    {...trackingDestinationItem}
                  />
                )}
                {featuredOffers.length !== 0 && (
                  <FareListTwoColumns
                    ItemComponentInstance={OfferHorizontalCard}
                    itemsData={featuredOffers}
                    {...components.featuredOffersList}
                    {...listItemsSettings}
                  />
                )}
              </Col>
            )}
        </Row>
        {currentComponent === 'all' && showDisclaimer && (
          <Row {...components.disclaimerContentSettings}>
            <Col>{this.disclaimer}</Col>
          </Row>
        )}
      </Box>
    );

    return renderContent;
  }
}

AirBnBFeaturedDestinationsContainerMobile.propTypes = {
  /**
   * To be used to set if the container is a controlled one
   */
  containerType: PropTypes.string,
  /**
   * ClassName of the components
   */
  className: PropTypes.string,
  /*
   * Action to be executed when clicking in fareItem
   */
  fareItemOnClickAction: PropTypes.func,
  /**
   * Function to getFares
   */
  getOffers: PropTypes.func,
  /**
   * Function for request to obtain airports
   */
  getCities: PropTypes.func,
  /**
   * Component Behavior when there are not results available
   */
  noFaresBehavior: PropTypes.oneOf(['hidden', 'showNoResults']),
  /**
   * Handle the action when change departure city
   */
  onChangeDepartureCity: PropTypes.func,
  /**
   * Handle the action when change date
   */
  onChangeDate: PropTypes.func,
  /**
   * Handle the action when change budget price
   */
  onChangeBudget: PropTypes.func,
  /**
   * Settings of the Pagination
   */
  paginationSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * Component PrePopulation
   */
  prePopulation: PropTypes.objectOf(PropTypes.any),
  /**
   * Determine if the component will show the disclaimer
   */
  showDisclaimer: PropTypes.bool,
  /**
   * Shows Loading Component
   */
  showLoading: PropTypes.bool,
  /**
   * Determine if the component will show the skeleton screen while
   * loading (Default value will be false)
   */
  showSkeletonScreen: PropTypes.bool,
  /**
   * Determine if the component will show the filters.
   */
  showFilters: PropTypes.bool,
  /**
   * Determine if the component will show the title
   */
  showTitle: PropTypes.bool,
  /**
   * Css style applied to the component
   */
  style: PropTypes.objectOf(PropTypes.any),
  /**
   * SkeletonSettings Component
   */
  skeletonSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * Props to translate
   */
  translate: PropTypes.func,
  /**
   * Direction of the component.
   */
  direction: PropTypes.oneOf(['rtl', 'ltr']),
  /**
   * Sub-Components settings
   */
  components: PropTypes.objectOf(PropTypes.any),
  /**
   * Container real-time data
   */
  data: PropTypes.objectOf(PropTypes.any),
  /**
   * Define if iata code in airport is show
   */
  showCityCode: PropTypes.bool,
  /**
   * Possible interactions will be Popup or Direct Deeplink
   */
  fareItemInteractionType: PropTypes.oneOf(['popup', 'deeplink']),
  /**
   * LocalizationData via moment to be used as a date translator
   * https://github.com/moment/moment/blob/develop/src/locale/es.js
   */
  localizationData: PropTypes.objectOf(PropTypes.any),
  /**
   * Theme object on the component
   */
  theme: PropTypes.objectOf(PropTypes.any),
  /**
   * Handle the action when change home type
   */
  onChangeHomeType: PropTypes.func,
  /**
   * Handle the action when change guest
   */
  onChangeGuests: PropTypes.func,
  /**
   * Parameters tracking
   */
  tracking: PropTypes.objectOf(PropTypes.any)
};

AirBnBFeaturedDestinationsContainerMobile.defaultProps = {
  fareItemInteractionType: 'popup',
  showDisclaimer: true,
  showLoading: true,
  showSkeletonScreen: true,
  showFilters: true,
  showTitle: true,
  components: {
    title: {
      fontSize: '5',
      fontWeight: 'bold',
      textAlign: 'other'
    },
    airBnBIconStyles: {
      fontSize: '25px'
    },
    mainFeaturedOffer: {
      style: {
        lineHeight: '2'
      },
      border: true,
      borderColor: 'primary',
      onClick: detailHouse
    },
    headerLocation: {
      textAlign: 'center',
      padding: '0',
      paddingRight: '4',
      fontSize: '5',
      fontWeight: 'bold'
    },
    featuredOfferItem: {
      style: {
        lineHeight: '2'
      },
      onClick: detailHouse
    },
    featuredOffersList: {
      marginTop: '2'
    },
    alert: {
      bgColor: 'light',
      textAlign: 'center',
      padding: '3',
      rounded: '@theme:card.rounded'
    },
    clearAll: {
      color: 'link',
      bgColor: 'transparent',
      borderNone: true
    },
    disclaimer: {},
    disclaimerContentSettings: {
      marginTop: '2'
    },
    filterContent: {
      marginTop: '1'
    },
    mainContent: {},
    noResultsPlaceHolder: {
      bgColor: 'light',
      rounded: '@theme:card.rounded'
    },
    guestsSelector: {
      marginBottom: '2',
      shadow: '@theme:input.shadow',
      rounded: '@theme:input.rounded'
    },
    loading: {},
    locationSelector: {
      showSelectorMenuArrow: false,
      clearable: true,
      showArrowsIfClearable: false,
      openOnFocus: true,
      itemCount: 10,
      icon: 'marker',
      marginBottom: '2',
      menuItem: {
        marginTop: '1',
        paddingHeight: '0',
        rounded: '0',
        border: false
      },
      inputItem: {
        shadow: '@theme:input.shadow',
        rounded: '@theme:input.rounded'
      },
      listItem: {
        hoverBgColor: 'primary',
        hoverTextColor: 'white',
        paddingLeft: '4',
        paddingRight: '1',
        paddingHeight: '2'
      },
      groupItem: {
        hoverBgColor: 'primary',
        hoverTextColor: 'white',
        paddingWidth: '1',
        paddingHeight: '2'
      },
      noResultItem: {
        paddingWidth: '1',
        paddingHeight: '2'
      }
    },
    datesRangeSelector: {
      marginBottom: '2',
      shadow: '@theme:input.shadow',
      rounded: '@theme:input.rounded'
    },
    budgetSetting: {
      marginBottom: '2',
      shadow: '@theme:input.shadow',
      rounded: '@theme:input.rounded'
    },
    mainFeaturedOfferLabel: {
      fontWeight: 'bold',
      textTransform: 'uppercase',
      bgColor: 'primary',
      textColor: 'white',
      padding: '1',
      paddingLeft: '2',
      fontSize: '10px'
    },
    moreFilter: {
      color: 'none',
      border: true,
      width: '100',
      marginBottom: '2',
      shadow: '@theme:input.shadow',
      rounded: '@theme:input.rounded',
      label: {
        marginLeft: '2'
      },
      style: {
        height: '44px'
      }
    },
    homeType: {
      paddingHeight: '2'
    },
    applyFilters: {
      color: 'dark',
      width: '100',
      marginBottom: '2',
      label: {
        marginLeft: '2'
      }
    },
    showLabelFilters: true,
    labelFilters: {
      fontSize: '4',
      fontWeight: 'bold'
    }
  },
  data: {
    initialDepartureAirports: null,
    initialOffers: null
  },
  containerType: 'stateful',
  noFaresBehavior: 'hidden',
  className: '',
  getOffers,
  getCities,
  onChangeDepartureCity: useChangeOrigin,
  onChangeDate: useChangeDateRange,
  onChangeBudget: useChangeBudget,
  onChangeHomeType: useChangeHomeType,
  fareItemOnClickAction: useClickListing,
  onChangeGuests: useChangeGuests,
  paginationSettings: {
    type: 'load_more',
    step: 10,
    limit: 30,
    intialVisibleItems: 10
  },
  prePopulation: {},
  style: {},
  skeletonSettings: {
    style: { backgroundColor: 'white' }
  },
  translate: () => {},
  direction: 'ltr',
  showCityCode: true,
  localizationData: null,
  theme: null,
  tracking: {
    flexibleDates: false,
    passengerCount: 1,
    promoCode: 'EVERYMUNDO',
    eventType: 'listingsFeaturedBackgroundImage'
  }
};
AirBnBFeaturedDestinationsContainerMobile.componentName =
  'AirBnBFeaturedDestinationsContainerMobile';

export default withDeepMergeDefaultProps(AirBnBFeaturedDestinationsContainerMobile);
